import { useEffect, useRef, useState } from "react";
import { range } from "shared/utils/arrayUtils";
import { FCWC } from "shared/utils/FCWC";
import styled from "styled-components";

const dashedLine = "3px";
const StickyControllerWrapper = styled.div<{
  $gap: string;
  $heightAndGap: string;
  $stickyBackground: string;
  $stickyContainers: number;
}>`
  ${({ $gap, $heightAndGap, $stickyBackground, $stickyContainers }) => `
  display: flex;
  flex-direction: column;
  gap: ${$gap};
  min-height: 100%;

  .sticky-container {
    position: sticky;
    z-index: 1001; /* Necessary since TrainGPS uses z-index: 1000 */
    margin-bottom: calc(-1 * ${$heightAndGap} - ${dashedLine});
    margin-top: -${$gap};
    padding-top: ${$gap};
    background-color: ${$stickyBackground};
  }

  ${range($stickyContainers)
    .map(
      (it) => `
      .sticky-container:nth-child(${it + 1} of .sticky-container) {
        top: calc(${it} * ${$heightAndGap} - ${$gap});
        bottom: calc(
          ${$stickyContainers - 1 - it} * ${$heightAndGap} - ${dashedLine}
        );
      }
    `,
    )
    .join("")}
`}
`;

type StickyControllerProps = {
  headerHeight: string;
  stickyBackground: string;
  gap: string;
};
export const StickyController: FCWC<StickyControllerProps> = ({
  headerHeight,
  stickyBackground,
  gap,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [stickyContainers, setStickyContainers] = useState(0);

  // Counts the amount of `.sticky-container`
  useEffect(() => {
    const updateCount = () => {
      if (wrapperRef.current) {
        const stickyElements =
          wrapperRef.current.querySelectorAll(".sticky-container");
        setStickyContainers(stickyElements.length);
      }
    };

    // Initial count
    updateCount();

    // Use MutationObserver to watch for changes in children
    const observer = new MutationObserver(updateCount);

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => observer.disconnect();
  }, [children]);

  const heightAndGap = `(${headerHeight} + ${gap})`;

  return (
    <StickyControllerWrapper
      ref={wrapperRef}
      $gap={gap}
      $heightAndGap={heightAndGap}
      $stickyContainers={stickyContainers}
      $stickyBackground={stickyBackground}
    >
      {children}
    </StickyControllerWrapper>
  );
};
